import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dagjhp9tto0ceqqnht3g";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success">("idle");
  const [formError, setFormError] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // honeypot check
    const honey = (form.elements.namedItem("company_alt") as HTMLInputElement | null)?.value;
    if (honey && honey.trim() !== "") {
      setStatus("success");
      setFormError("");
      form.reset();
      setCount(0);
      return;
    }

    const fd = new FormData(form);
    fd.delete("company_alt");
    setFormError("");
    setStatus("submitting");

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const msg =
        (parsed as any)?.meta?.message ||
        (parsed as any)?.message ||
        (parsed as any)?.meta?.detail ||
        responseText;

      if (!res.ok) {
        setStatus("idle");
        setFormError(
          typeof msg === "string" && msg.trim()
            ? msg
            : "提交失败，请稍后再试。"
        );
        return;
      }
      const code = (parsed as any)?.code;
      const bodyHasSpam =
        typeof responseText === "string" &&
        /spam|form data is spam/i.test(responseText);
      if (code !== "OK" || bodyHasSpam || (parsed && !code)) {
        setStatus("idle");
        setFormError(
          typeof msg === "string" && msg.trim()
            ? msg
            : "提交未成功，请检查填写内容后重试。"
        );
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
      setCount(0);
    } catch {
      setStatus("idle");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* info */}
          <div className="lg:col-span-5">
            <p className="text-primary-600 font-semibold tracking-wide text-sm">CONTACT · 投稿与联系</p>
            <h2 className="mt-2 font-heading text-3xl md:text-4xl font-extrabold text-foreground-950">
              发现新瓜，或想给我们提意见？
            </h2>
            <p className="mt-3 text-foreground-600 leading-7">
              欢迎投稿你在街头实拍争议、路人隐私、下架事件中看到的最新线索，或对本号的选题与写作提出批评建议。
              我们重视每一位读者的来信。
            </p>

            <ul className="mt-7 space-y-4">
              <li className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200">
                <span className="w-11 h-11 shrink-0 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center text-xl">
                  <i className="ri-mail-send-line"></i>
                </span>
                <div>
                  <p className="text-sm text-foreground-500">投稿邮箱</p>
                  <a href="mailto:hello@dashanchigua.example.com" className="text-foreground-900 font-medium hover:text-primary-600">
                    hello@dashanchigua.example.com
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200">
                <span className="w-11 h-11 shrink-0 rounded-xl bg-accent-500 text-background-50 flex items-center justify-center text-xl">
                  <i className="ri-chat-3-line"></i>
                </span>
                <div>
                  <p className="text-sm text-foreground-500">新媒体合作</p>
                  <a href="mailto:pr@dashanchigua.example.com" className="text-foreground-900 font-medium hover:text-primary-600">
                    pr@dashanchigua.example.com
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200">
                <span className="w-11 h-11 shrink-0 rounded-xl bg-foreground-900 text-background-50 flex items-center justify-center text-xl">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-sm text-foreground-500">响应时间</p>
                  <p className="text-foreground-900 font-medium">工作日 24 小时内回复</p>
                </div>
              </li>
            </ul>
          </div>

          {/* form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-readdy-form="搭讪吃瓜-投稿联系"
              className="p-6 md:p-8 rounded-3xl bg-background-50 border border-background-200"
            >
              <h3 className="font-heading text-xl font-bold text-foreground-950">在线投稿 / 反馈表单</h3>
              <p className="mt-1.5 text-sm text-foreground-500">
                请留下联系方式，便于我们与你核实细节（选填，将严格保密）。
              </p>

              {/* honeypot */}
              <div className="form-honey-field">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="c-name" className="block text-sm font-medium text-foreground-800 mb-1.5">称呼</label>
                  <input
                    id="c-name"
                    name="name"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="你的昵称或称呼"
                    className="w-full h-11 px-4 rounded-xl bg-background-100 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="c-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                  <input
                    id="c-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="用于回复你"
                    className="w-full h-11 px-4 rounded-xl bg-background-100 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="c-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">反馈类型</label>
                <select
                  id="c-subject"
                  name="subject"
                  required
                  className="w-full h-11 px-4 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="">请选择投稿类别</option>
                  <option value="争议线索">搭讪实拍争议线索</option>
                  <option value="隐私问题">路人隐私相关问题</option>
                  <option value="下架事件">搭讪视频下架事件</option>
                  <option value="选题建议">深度选题建议</option>
                  <option value="意见反馈">对网站的批评建议</option>
                  <option value="媒体合作">媒体与商务合作</option>
                </select>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <label htmlFor="c-msg" className="block text-sm font-medium text-foreground-800 mb-1.5">具体内容</label>
                  <span className="text-xs text-foreground-400">{count}/500</span>
                </div>
                <textarea
                  id="c-msg"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  onChange={(e) => setCount(e.target.value.length)}
                  placeholder="请尽量提供时间、地点、平台等可核实信息（涉隐私内容将匿名处理）……"
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-y"
                ></textarea>
              </div>

              {formError && (
                <p className="mt-3 flex items-center gap-2 text-sm text-red-600" role="alert">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-6 h-12 w-full sm:w-auto px-8 inline-flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-[15px] font-medium transition whitespace-nowrap"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i> 提交投稿
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="mt-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3" role="status">
                  <i className="ri-checkbox-circle-fill"></i>
                  收到！感谢你的投稿与支持，我们会尽快处理并与你联系。
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}